import React, { useState } from 'react';
import { FailedTransactionDetail } from '../../types/admin.types';
import { adminService } from '../../services/adminService';
import './TransactionDetailModal.css';

interface Props {
  transaction: FailedTransactionDetail;
  onClose: () => void;
  onReprocessSuccess: () => void;
}

const TransactionDetailModal: React.FC<Props> = ({ transaction, onClose, onReprocessSuccess }) => {
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessResult, setReprocessResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showJsonRaw, setShowJsonRaw] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleReprocess = async (force: boolean = false) => {
    if (!window.confirm(
      force
        ? '¿Estás seguro de FORZAR el reprocesamiento de esta transacción? Esto la procesará incluso si ya está completada.'
        : '¿Estás seguro de reprocesar esta transacción? Asegúrate de que el error en SAP ya fue corregido.'
    )) {
      return;
    }

    try {
      setReprocessing(true);
      setReprocessResult(null);

      const result = await adminService.reprocessTransaction(transaction.TransactionId, force);

      setReprocessResult({
        success: result.Success,
        message: result.Message
      });

      if (result.Success) {
        setTimeout(() => {
          onReprocessSuccess();
        }, 2000);
      }
    } catch (error: any) {
      setReprocessResult({
        success: false,
        message: error.message || 'Error al reprocesar'
      });
    } finally {
      setReprocessing(false);
    }
  };

  const copyJsonToClipboard = () => {
    if (transaction.WebhookPayload) {
      navigator.clipboard.writeText(transaction.WebhookPayload);
      alert('JSON copiado al portapapeles');
    }
  };

  // Parsear el JSON para mostrarlo formateado
  let parsedJson: any = null;
  try {
    if (transaction.WebhookPayload) {
      parsedJson = JSON.parse(transaction.WebhookPayload);
    }
  } catch (error) {
    console.error('Error parsing JSON:', error);
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content transaction-detail-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Detalle de Transacción Fallida #{transaction.TransactionId}</h2>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Información General */}
          <div className="detail-section">
            <h3>📋 Información General</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Transaction ID:</label>
                <span className="value">{transaction.TransactionId}</span>
              </div>
              <div className="detail-item">
                <label>Bold Transaction ID:</label>
                <span className="value mono">{transaction.BoldTransactionId}</span>
              </div>
              <div className="detail-item">
                <label>Order ID:</label>
                <span className="value mono">{transaction.BoldOrderId}</span>
              </div>
              <div className="detail-item">
                <label>Estado:</label>
                <span className={`status-badge status-${transaction.Status}`}>
                  {transaction.Status}
                </span>
              </div>
              <div className="detail-item">
                <label>Reintentos:</label>
                <span className="value">{transaction.RetryCount}</span>
              </div>
              <div className="detail-item">
                <label>Fecha Creación:</label>
                <span className="value">{formatDate(transaction.CreatedAt)}</span>
              </div>
            </div>
          </div>

          {/* Información del Vehículo */}
          <div className="detail-section">
            <h3>🚗 Información del Vehículo</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Placa:</label>
                <span className="value highlight">{transaction.Placa}</span>
              </div>
            </div>
          </div>

          {/* Información del Cliente */}
          <div className="detail-section">
            <h3>👤 Información del Cliente</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Nombre:</label>
                <span className="value">{transaction.CustomerName || 'No especificado'}</span>
              </div>
              <div className="detail-item">
                <label>Identificación:</label>
                <span className="value">{transaction.CustomerIdentification}</span>
              </div>
              <div className="detail-item">
                <label>Tipo ID:</label>
                <span className="value">{transaction.CustomerIdentificationType || 'CC'}</span>
              </div>
              <div className="detail-item">
                <label>Email:</label>
                <span className="value">{transaction.CustomerEmail || 'No especificado'}</span>
              </div>
              <div className="detail-item">
                <label>Teléfono:</label>
                <span className="value">{transaction.CustomerPhone || 'No especificado'}</span>
              </div>
              <div className="detail-item">
                <label>CardCode SAP:</label>
                <span className="value">{transaction.CardCode || 'No asignado'}</span>
              </div>
            </div>
          </div>

          {/* Información del Pago */}
          <div className="detail-section">
            <h3>💳 Información del Pago</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Monto:</label>
                <span className="value amount">{formatCurrency(transaction.Amount)}</span>
              </div>
              <div className="detail-item">
                <label>Moneda:</label>
                <span className="value">{transaction.Currency}</span>
              </div>
              <div className="detail-item">
                <label>Método:</label>
                <span className="value">{transaction.PaymentMethod || 'No especificado'}</span>
              </div>
              <div className="detail-item">
                <label>Marca Tarjeta:</label>
                <span className="value">{transaction.PaymentBrand || 'No especificado'}</span>
              </div>
              <div className="detail-item">
                <label>Últimos 4 Dígitos:</label>
                <span className="value">**** {transaction.CardLastFour || '****'}</span>
              </div>
            </div>
          </div>

          {/* Información SAP */}
          {(transaction.SapInvoiceDocNum || transaction.SapPaymentDocNum) && (
            <div className="detail-section">
              <h3>🏢 Información SAP B1</h3>
              <div className="detail-grid">
                {transaction.SapInvoiceDocNum && (
                  <>
                    <div className="detail-item">
                      <label>Factura DocEntry:</label>
                      <span className="value">{transaction.SapInvoiceDocEntry}</span>
                    </div>
                    <div className="detail-item">
                      <label>Factura DocNum:</label>
                      <span className="value highlight">#{transaction.SapInvoiceDocNum}</span>
                    </div>
                  </>
                )}
                {transaction.SapPaymentDocNum && (
                  <>
                    <div className="detail-item">
                      <label>Pago DocEntry:</label>
                      <span className="value">{transaction.SapPaymentDocEntry}</span>
                    </div>
                    <div className="detail-item">
                      <label>Pago DocNum:</label>
                      <span className="value highlight">#{transaction.SapPaymentDocNum}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Error Message */}
          {transaction.ErrorMessage && (
            <div className="detail-section error-section">
              <h3>⚠️ Mensaje de Error</h3>
              <div className="error-box">
                <pre>{transaction.ErrorMessage}</pre>
              </div>
            </div>
          )}

          {/* JSON del Webhook */}
          {transaction.WebhookPayload && (
            <div className="detail-section json-section">
              <div className="json-header">
                <h3>📄 JSON del Webhook Original</h3>
                <div className="json-actions">
                  <button
                    className="btn-toggle-json"
                    onClick={() => setShowJsonRaw(!showJsonRaw)}
                  >
                    {showJsonRaw ? '📋 Ver Formateado' : '📝 Ver Raw'}
                  </button>
                  <button className="btn-copy-json" onClick={copyJsonToClipboard}>
                    📋 Copiar JSON
                  </button>
                </div>
              </div>
              <div className="json-box">
                {showJsonRaw ? (
                  <pre className="json-raw">{transaction.WebhookPayload}</pre>
                ) : (
                  <pre className="json-formatted">{JSON.stringify(parsedJson, null, 2)}</pre>
                )}
              </div>
              <p className="json-note">
                💡 <strong>Nota:</strong> Este es el JSON original enviado por Bold.
                El sistema utilizará este mismo JSON al reprocesar la transacción.
              </p>
            </div>
          )}

          {/* Resultado de Reprocesamiento */}
          {reprocessResult && (
            <div className={`result-message ${reprocessResult.success ? 'success' : 'error'}`}>
              <strong>{reprocessResult.success ? '✅' : '❌'}</strong>
              {reprocessResult.message}
            </div>
          )}
        </div>

        {/* Footer - Acciones */}
        <div className="modal-footer">
          <div className="footer-info">
            <p className="info-text">
              ⚠️ Antes de reprocesar, asegúrate de haber corregido el error manualmente en SAP.
            </p>
          </div>
          <div className="footer-actions">
            <button className="btn-cancel" onClick={onClose}>
              Cerrar
            </button>
            <button
              className="btn-reprocess"
              onClick={() => handleReprocess(false)}
              disabled={reprocessing}
            >
              {reprocessing ? '⏳ Reprocesando...' : '🔄 Reprocesar Transacción'}
            </button>
            {transaction.Status === 'completed' && (
              <button
                className="btn-force-reprocess"
                onClick={() => handleReprocess(true)}
                disabled={reprocessing}
              >
                ⚡ Forzar Reproceso
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailModal;
